import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { keys, RequestError, type ApiError, type StepDoc, type Tree } from "@/app/queries";

/* Мутации структуры курса для дерева A1. Хуков в queries.ts нет —
 * это локальные помощники конструктора. */

function fail(status: number, error: unknown, fallback: string): never {
  const e = error as Partial<ApiError> | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? fallback);
}

/** Позиции шагов сдвигаются у соседей: открытый черновик мог сменить номер. */
const DRAFTS = ["admin", "draft"] as const;

/** «+ шаг» / вставка: POST /admin/tracks/{trackId}/steps. Без position — в конец трека. */
export async function createStep(trackId: string, title: string, position?: number): Promise<StepDoc> {
  const body = position === undefined ? { title } : { title, position };
  const { data, error, response } = await api.POST("/admin/tracks/{trackId}/steps", { params: { path: { trackId } }, body });
  if (!data) fail(response.status, error, "Не удалось создать шаг.");
  return data;
}

export function useCreateStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trackId, title, position }: { trackId: string; title: string; position?: number }) => createStep(trackId, title, position),
    onSuccess: (doc, vars) => {
      qc.setQueryData(keys.draft(doc.id), doc);
      void qc.invalidateQueries({ queryKey: keys.tree });
      if (vars.position !== undefined) void qc.invalidateQueries({ queryKey: DRAFTS });
    },
  });
}

export type TrackInput = { slug: string; name: string; position: number; parallel: boolean };

/** «+ трек»: POST /admin/tracks. Сервер делает upsert по slug — занятый slug
 *  перезаписал бы чужой трек, поэтому форма его не пропускает. */
export function useCreateTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TrackInput) => {
      const { data, error, response } = await api.POST("/admin/tracks", { body });
      if (!data) fail(response.status, error, "Не удалось создать трек.");
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.tree }),
  });
}

/** Порядок треков: PUT /admin/tracks/order — полный список id. */
export function useReorderTracks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error, response } = await api.PUT("/admin/tracks/order", { body: { ids } });
      if (!data) fail(response.status, error, "Не удалось поменять порядок треков.");
      return data;
    },
    onSuccess: (tree: Tree) => qc.setQueryData(keys.tree, tree),
  });
}

/** Удаление трека: DELETE /admin/tracks/{trackId}. Сервер отвечает 409, если
 *  по шагам трека есть прогресс студентов или домашка. */
export function useDeleteTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (trackId: string) => {
      const { data, error, response } = await api.DELETE("/admin/tracks/{trackId}", { params: { path: { trackId } } });
      if (!data) fail(response.status, error, "Не удалось удалить трек.");
      return data;
    },
    onSuccess: (tree: Tree) => qc.setQueryData(keys.tree, tree),
  });
}

/** Перенос шага: PUT /admin/steps/{stepId}/move {track_id, position}. */
export function useMoveStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepId, trackId, position }: { stepId: string; trackId: string; position: number }) => {
      const { data, error, response } = await api.PUT("/admin/steps/{stepId}/move", { params: { path: { stepId } }, body: { track_id: trackId, position } });
      if (!data) fail(response.status, error, "Не удалось перенести шаг.");
      return data;
    },
    onSuccess: (tree: Tree) => {
      qc.setQueryData(keys.tree, tree);
      void qc.invalidateQueries({ queryKey: DRAFTS });
    },
  });
}

const TRANSLIT: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
  с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

/** slug трека из названия: латиница и цифры, кириллица транслитом, остальное — дефис. */
export function slugify(name: string): string {
  return [...name.toLowerCase()]
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** slug, которого ещё нет среди треков: ml → ml-2 → ml-3. */
export function uniqueSlug(slug: string, taken: string[]): string {
  if (!slug) return "";
  const set = new Set(taken);
  if (!set.has(slug)) return slug;
  let n = 2;
  while (set.has(`${slug}-${n}`)) n++;
  return `${slug}-${n}`;
}

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
